import { useEffect, useState } from 'react'

// Build id baked into the bundle at build time (see vite.config.js `define`).
// In dev / tests it is undefined, which disables version checking.
// eslint-disable-next-line no-undef
const RUNNING_BUILD_ID = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000

/**
 * True when a newer release is deployed than the one currently running (#533).
 * Guards against dev/empty ids so we never prompt without a real comparison.
 */
export function isUpdateAvailable(runningId, deployedId) {
  if (!runningId || runningId === 'dev') return false
  if (!deployedId) return false
  return deployedId !== runningId
}

/**
 * Fetches the deployed build id from /version.json (cache-busted, no-store).
 * Returns null on any failure so callers can treat "unknown" as "no update".
 */
export async function fetchDeployedBuildId(fetchFn = fetch) {
  const res = await fetchFn(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
  if (!res || !res.ok) return null
  const data = await res.json()
  return data && data.buildId ? data.buildId : null
}

/**
 * Polls version.json on an interval and whenever the tab regains focus, and
 * flips to true once a newer release is available. Once true it stays true.
 */
export function useVersionCheck({ intervalMs = DEFAULT_INTERVAL_MS, runningId = RUNNING_BUILD_ID } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    // No baked build id (dev/test) → nothing meaningful to compare against.
    if (!runningId || runningId === 'dev') return undefined

    let cancelled = false

    const check = async () => {
      try {
        const deployedId = await fetchDeployedBuildId()
        if (!cancelled && isUpdateAvailable(runningId, deployedId)) {
          setUpdateAvailable(true)
        }
      } catch {
        // Network blip — ignore and try again on the next tick.
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check()
    }

    const intervalId = setInterval(check, intervalMs)
    document.addEventListener('visibilitychange', onVisibility)
    check()

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [runningId, intervalMs])

  return updateAvailable
}
